/**
 * Smart Profile Editor - State Management & Dependency Engine
 * 
 * This reducer handles all profile state changes and automatically
 * calculates cascading effects when dependencies change.
 */

// Action Types
export const ACTIONS = {
  // Data loading
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  LOAD_PROFILE: 'LOAD_PROFILE',
  LOAD_REFERENCE_DATA: 'LOAD_REFERENCE_DATA',
  
  // User info
  UPDATE_ZIP_CODE: 'UPDATE_ZIP_CODE',
  UPDATE_ISP: 'UPDATE_ISP',
  UPDATE_ELIGIBILITY: 'UPDATE_ELIGIBILITY',
  UPDATE_PROFILE_FIELD: 'UPDATE_PROFILE_FIELD',
  SYNC_PROFILE_FROM_SAVE: 'SYNC_PROFILE_FROM_SAVE', // Updates profile without marking dirty
  
  // Credit cards
  ADD_CREDIT_CARD: 'ADD_CREDIT_CARD',
  REMOVE_CREDIT_CARD: 'REMOVE_CREDIT_CARD',
  UPDATE_CREDIT_CARD: 'UPDATE_CREDIT_CARD',
  UPDATE_CARD_ALLOCATION: 'UPDATE_CARD_ALLOCATION',
  
  // Memberships
  ADD_MEMBERSHIP: 'ADD_MEMBERSHIP',
  REMOVE_MEMBERSHIP: 'REMOVE_MEMBERSHIP',
  UPDATE_MEMBERSHIP: 'UPDATE_MEMBERSHIP',
  
  // Subscriptions
  ADD_SUBSCRIPTION: 'ADD_SUBSCRIPTION',
  REMOVE_SUBSCRIPTION: 'REMOVE_SUBSCRIPTION',
  UPDATE_SUBSCRIPTION: 'UPDATE_SUBSCRIPTION',
  
  // Bundles
  ADD_BUNDLE: 'ADD_BUNDLE',
  REMOVE_BUNDLE: 'REMOVE_BUNDLE',
  UPDATE_BUNDLE: 'UPDATE_BUNDLE', // NEW: Update bundle properties like monthly_cost
  UPDATE_BUNDLE_ALLOCATION: 'UPDATE_BUNDLE_ALLOCATION',
  
  // Dependencies
  SET_PENDING_CHANGES: 'SET_PENDING_CHANGES',
  CONFIRM_PENDING_CHANGES: 'CONFIRM_PENDING_CHANGES',
  CANCEL_PENDING_CHANGES: 'CANCEL_PENDING_CHANGES',
  
  // Save state
  SET_SAVING: 'SET_SAVING',
  SET_SECTION_DIRTY: 'SET_SECTION_DIRTY',
  CLEAR_SECTION_DIRTY: 'CLEAR_SECTION_DIRTY',
  
  // UI state
  SET_ACTIVE_SECTION: 'SET_ACTIVE_SECTION',
  TOGGLE_SECTION: 'TOGGLE_SECTION',
};

// Initial state
export const initialState = {
  // Loading states
  loading: true,
  saving: false,
  error: null,
  
  // User profile data
  profile: {
    userId: null,
    email: '',
    fullName: '',
    zipCode: '',
    internetProvider: '',
    internetPlan: '',
    internetMonthlyCost: '',
    cellularProvider: '',
    cellularPlan: '',
    cellularMonthlyCost: '',
    cellularLines: 1,
    planType: 'free',
    queriesUsedMonth: 0,
    eligibility: {
      isStudent: false,
      isSenior: false,
      isVeteran: false,
      isFirstResponder: false,
    },
  },
  
  // Related entities
  creditCards: [],
  memberships: [],
  subscriptions: [],
  bundles: [], // Active bundles the user has
  allocations: [], // Credit card allocations
  activePerks: [],
  
  // Reference data (from database)
  refData: {
    streamingServices: [],
    ispPerks: [],
    cardPerks: [],
    membershipPerks: [],
    bundles: [],
    membershipsRef: [],
  },
  
  // Dependency tracking
  pendingChanges: null, // { type, oldValue, newValue, impacts: [...] }
  
  // UI state
  activeSection: null,
  expandedSections: ['subscriptions'], // Default expanded
  dirtySections: new Set(), // Sections with unsaved changes
};

/**
 * Calculate impacts when ISP changes
 */
function calculateISPChangeImpacts(state, oldISP, newISP) {
  const impacts = [];
  const { refData, subscriptions, activePerks } = state;
  
  // Find perks that will be lost
  const oldISPPerks = refData.ispPerks.filter(p => 
    p.isp_name?.toLowerCase() === oldISP?.toLowerCase()
  );
  
  const lostPerks = oldISPPerks.filter(perk => {
    const hasPerk = activePerks.some(ap => 
      ap.perk_source?.toLowerCase().includes(oldISP?.toLowerCase()) &&
      ap.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
    );
    return hasPerk;
  });
  
  lostPerks.forEach(perk => {
    const sub = subscriptions.find(s => 
      s.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
    );
    
    impacts.push({
      type: 'LOSE_PERK',
      service: perk.service_name,
      tier: perk.tier,
      source: oldISP,
      monthlyCost: sub?.monthly_cost || 0,
      message: `You'll lose free ${perk.service_name} from ${oldISP}`,
    });
  });
  
  // Find perks that will be gained
  const newISPPerks = refData.ispPerks.filter(p => 
    p.isp_name?.toLowerCase() === newISP?.toLowerCase()
  );
  
  newISPPerks.forEach(perk => {
    const alreadyHas = subscriptions.some(s => 
      s.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
    );
    
    impacts.push({
      type: alreadyHas ? 'UPGRADE_TO_FREE' : 'GAIN_PERK',
      service: perk.service_name,
      tier: perk.tier,
      source: newISP,
      monthlySavings: alreadyHas ? 
        subscriptions.find(s => s.service_name?.toLowerCase() === perk.service_name?.toLowerCase())?.monthly_cost || 0 
        : 0,
      message: alreadyHas 
        ? `${perk.service_name} will now be FREE via ${newISP}`
        : `You'll get free ${perk.service_name} with ${newISP}`,
    });
  });
  
  return impacts;
}

/**
 * Calculate impacts when a credit card is removed
 */
function calculateCardRemovalImpacts(state, cardId) {
  const impacts = [];
  const { creditCards, subscriptions, allocations } = state;
  
  const card = creditCards.find(c => c.id === cardId);
  if (!card) return impacts;
  
  // Find allocations that will be lost
  const cardAllocations = allocations.filter(a => a.credit_card_id === cardId);
  
  cardAllocations.forEach(allocation => {
    const sub = subscriptions.find(s => s.id === allocation.subscription_id);
    if (sub) {
      impacts.push({
        type: 'LOSE_CREDIT',
        service: sub.service_name,
        amount: allocation.amount_allocated,
        card: card.card_name,
        newMonthlyCost: sub.monthly_cost || 0,
        message: `${sub.service_name} will cost $${(sub.monthly_cost || 0).toFixed(2)}/mo without ${card.card_name} credit`,
      });
    }
  });
  
  return impacts;
}

/**
 * Calculate impacts when a membership is removed
 */
function calculateMembershipRemovalImpacts(state, membershipId) {
  const impacts = [];
  const { memberships, subscriptions, activePerks } = state;
  
  const membership = memberships.find(m => m.id === membershipId);
  if (!membership) return impacts;
  
  // Find perks/subscriptions that depend on this membership
  const dependentPerks = activePerks.filter(p => 
    p.perk_source?.toLowerCase().includes(membership.membership_type?.toLowerCase()) ||
    p.perk_source?.toLowerCase().includes(membership.membership_name?.toLowerCase())
  );
  
  dependentPerks.forEach(perk => {
    const sub = subscriptions.find(s => 
      s.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
    );
    
    impacts.push({
      type: 'LOSE_MEMBERSHIP_PERK',
      service: perk.service_name,
      membership: membership.membership_name,
      monthlyCost: sub?.monthly_cost || 0,
      message: `You'll lose free ${perk.service_name} from ${membership.membership_name}`,
    });
  });
  
  return impacts;
}

/**
 * Calculate NET impact summary for credit reallocation
 */
export function calculateReallocationImpact(currentAllocations, newAllocations, subscriptions, card) {
  let totalSavings = 0;
  let totalNewCosts = 0;
  const changes = [];
  
  // Map current allocations by subscription
  const currentBySubId = {};
  currentAllocations.forEach(a => {
    currentBySubId[a.subscription_id] = a.amount_allocated;
  });
  
  // Map new allocations by subscription
  const newBySubId = {};
  newAllocations.forEach(a => {
    newBySubId[a.subscription_id] = a.amount_allocated;
  });
  
  // Find all affected subscriptions
  const allSubIds = new Set([
    ...Object.keys(currentBySubId),
    ...Object.keys(newBySubId)
  ]);
  
  allSubIds.forEach(subId => {
    const sub = subscriptions.find(s => s.id === subId);
    if (!sub) return;
    
    const oldCredit = parseFloat(currentBySubId[subId] || 0);
    const newCredit = parseFloat(newBySubId[subId] || 0);
    const serviceCost = parseFloat(sub.monthly_cost || 0);
    
    if (oldCredit !== newCredit) {
      const oldOutOfPocket = Math.max(0, serviceCost - oldCredit);
      const newOutOfPocket = Math.max(0, serviceCost - newCredit);
      const difference = newOutOfPocket - oldOutOfPocket;
      
      if (difference > 0) {
        totalNewCosts += difference;
        changes.push({
          service: sub.service_name,
          type: 'COST_INCREASE',
          amount: difference,
          message: `${sub.service_name}: +$${difference.toFixed(2)}/mo`,
        });
      } else if (difference < 0) {
        totalSavings += Math.abs(difference);
        changes.push({
          service: sub.service_name,
          type: 'COST_DECREASE',
          amount: Math.abs(difference),
          message: `${sub.service_name}: -$${Math.abs(difference).toFixed(2)}/mo`,
        });
      }
    }
  });
  
  return {
    totalSavings,
    totalNewCosts,
    netImpact: totalSavings - totalNewCosts,
    changes,
    isPositive: totalSavings >= totalNewCosts,
  };
}

/**
 * Main reducer function
 */
export function profileReducer(state, action) {
  switch (action.type) {
    // ==================
    // Loading & Errors
    // ==================
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
      
    case ACTIONS.SET_SAVING:
      return { ...state, saving: action.payload };
    
    // ==================
    // Data Loading
    // ==================
    case ACTIONS.LOAD_PROFILE:
      return {
        ...state,
        loading: false,
        profile: { ...state.profile, ...action.payload.profile },
        creditCards: action.payload.creditCards || [],
        memberships: action.payload.memberships || [],
        subscriptions: action.payload.subscriptions || [],
        allocations: action.payload.allocations || [],
        activePerks: action.payload.activePerks || [],
        bundles: action.payload.bundles || [],
      };
      
    case ACTIONS.LOAD_REFERENCE_DATA:
      return {
        ...state,
        refData: { ...state.refData, ...action.payload },
      };
    
    // ==================
    // ISP Changes
    // ==================
    case ACTIONS.UPDATE_ISP: {
      const { newISP, skipImpactCheck } = action.payload;
      const oldISP = state.profile.internetProvider;
      
      // If same value, no change needed
      if (oldISP === newISP) return state;
      
      // Calculate impacts if not skipping
      if (!skipImpactCheck && oldISP) {
        const impacts = calculateISPChangeImpacts(state, oldISP, newISP);
        
        if (impacts.length > 0) {
          return {
            ...state,
            pendingChanges: {
              type: 'ISP_CHANGE',
              oldValue: oldISP,
              newValue: newISP,
              impacts,
            },
          };
        }
      }
      
      // No impacts or skipping check - apply directly
      return {
        ...state,
        profile: { ...state.profile, internetProvider: newISP },
        dirtySections: new Set([...state.dirtySections, 'isp']),
      };
    }

    case ACTIONS.UPDATE_PROFILE_FIELD: {
      const { field, value } = action.payload;
      return {
        ...state,
        profile: { ...state.profile, [field]: value },
        dirtySections: new Set([...state.dirtySections, 'isp']),
      };
    }

    case ACTIONS.SYNC_PROFILE_FROM_SAVE: {
      // Update profile fields without marking dirty (used after successful save)
      const updates = action.payload;
      const newDirtySections = new Set(state.dirtySections);
      newDirtySections.delete('profile');
      newDirtySections.delete('isp');
      
      return {
        ...state,
        profile: { 
          ...state.profile, 
          ...updates,
        },
        dirtySections: newDirtySections,
      };
    }
    
    // ==================
    // Credit Cards
    // ==================
    case ACTIONS.ADD_CREDIT_CARD:
      return {
        ...state,
        creditCards: [...state.creditCards, action.payload],
        dirtySections: new Set([...state.dirtySections, 'creditCards']),
      };
      
    case ACTIONS.REMOVE_CREDIT_CARD: {
      const { cardId, skipImpactCheck } = action.payload;
      
      if (!skipImpactCheck) {
        const impacts = calculateCardRemovalImpacts(state, cardId);
        
        if (impacts.length > 0) {
          return {
            ...state,
            pendingChanges: {
              type: 'REMOVE_CARD',
              cardId,
              impacts,
            },
          };
        }
      }
      
      return {
        ...state,
        creditCards: state.creditCards.filter(c => c.id !== cardId),
        allocations: state.allocations.filter(a => a.credit_card_id !== cardId),
        dirtySections: new Set([...state.dirtySections, 'creditCards']),
      };
    }
    
    case ACTIONS.UPDATE_CREDIT_CARD:
      return {
        ...state,
        creditCards: state.creditCards.map(c => 
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        ),
        dirtySections: new Set([...state.dirtySections, 'creditCards']),
      };
      
    case ACTIONS.UPDATE_CARD_ALLOCATION: {
      const { cardId, subscriptionId, amount } = action.payload;
      const existingIndex = state.allocations.findIndex(
        a => a.credit_card_id === cardId && a.subscription_id === subscriptionId
      );
      
      let newAllocations;
      if (amount === 0 || amount === null) {
        // Remove allocation
        newAllocations = state.allocations.filter(
          a => !(a.credit_card_id === cardId && a.subscription_id === subscriptionId)
        );
      } else if (existingIndex >= 0) {
        // Update existing
        newAllocations = state.allocations.map((a, i) => 
          i === existingIndex ? { ...a, amount_allocated: amount } : a
        );
      } else {
        // Add new
        newAllocations = [...state.allocations, {
          credit_card_id: cardId,
          subscription_id: subscriptionId,
          amount_allocated: amount,
          user_id: state.profile.userId,
        }];
      }
      
      // Also update the subscription's credit_applied and actual_cost
      const sub = state.subscriptions.find(s => s.id === subscriptionId);
      let newSubscriptions = state.subscriptions;
      
      if (sub) {
        const totalCreditForSub = newAllocations
          .filter(a => a.subscription_id === subscriptionId)
          .reduce((sum, a) => sum + parseFloat(a.amount_allocated || 0), 0);
        
        newSubscriptions = state.subscriptions.map(s => 
          s.id === subscriptionId 
            ? { 
                ...s, 
                credit_applied: totalCreditForSub,
                actual_cost: Math.max(0, (s.monthly_cost || 0) - totalCreditForSub)
              }
            : s
        );
      }
      
      return {
        ...state,
        allocations: newAllocations,
        subscriptions: newSubscriptions,
        // Don't mark dirty - save happens immediately
      };
    }
    
    case ACTIONS.UPDATE_BUNDLE_ALLOCATION: {
      const { bundleId, creditApplied, actualCost } = action.payload;
      
      const newBundles = state.bundles.map(b => 
        b.id === bundleId 
          ? { ...b, credit_applied: creditApplied, actual_cost: actualCost }
          : b
      );
      
      return {
        ...state,
        bundles: newBundles,
        // Don't mark dirty - save happens immediately
      };
    }
    
    // ==================
    // Memberships
    // ==================
    case ACTIONS.ADD_MEMBERSHIP:
      return {
        ...state,
        memberships: [...state.memberships, action.payload],
        dirtySections: new Set([...state.dirtySections, 'memberships']),
      };
      
    case ACTIONS.REMOVE_MEMBERSHIP: {
      const { membershipId, skipImpactCheck } = action.payload;
      
      if (!skipImpactCheck) {
        const impacts = calculateMembershipRemovalImpacts(state, membershipId);
        
        if (impacts.length > 0) {
          return {
            ...state,
            pendingChanges: {
              type: 'REMOVE_MEMBERSHIP',
              membershipId,
              impacts,
            },
          };
        }
      }
      
      return {
        ...state,
        memberships: state.memberships.filter(m => m.id !== membershipId),
        dirtySections: new Set([...state.dirtySections, 'memberships']),
      };
    }
    
    case ACTIONS.UPDATE_MEMBERSHIP:
      return {
        ...state,
        memberships: state.memberships.map(m => 
          m.id === action.payload.id ? { ...m, ...action.payload } : m
        ),
        dirtySections: new Set([...state.dirtySections, 'memberships']),
      };
    
    // ==================
    // Subscriptions
    // ==================
    case ACTIONS.ADD_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: [...state.subscriptions, action.payload],
        dirtySections: new Set([...state.dirtySections, 'subscriptions']),
      };
      
    case ACTIONS.REMOVE_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: state.subscriptions.filter(s => s.id !== action.payload),
        allocations: state.allocations.filter(a => a.subscription_id !== action.payload),
        dirtySections: new Set([...state.dirtySections, 'subscriptions']),
      };
      
    case ACTIONS.UPDATE_SUBSCRIPTION:
      return {
        ...state,
        subscriptions: state.subscriptions.map(s => 
          s.id === action.payload.id ? { ...s, ...action.payload } : s
        ),
        dirtySections: new Set([...state.dirtySections, 'subscriptions']),
      };
    
    // ==================
    // User Profile
    // ==================
    case ACTIONS.UPDATE_ZIP_CODE:
      return {
        ...state,
        profile: { ...state.profile, zipCode: action.payload },
        dirtySections: new Set([...state.dirtySections, 'profile']),
      };
      
    case ACTIONS.UPDATE_ELIGIBILITY:
      return {
        ...state,
        profile: {
          ...state.profile,
          eligibility: { ...state.profile.eligibility, ...action.payload },
        },
        dirtySections: new Set([...state.dirtySections, 'eligibility']),
      };
    
    // ==================
    // Bundles
    // ==================
    case ACTIONS.ADD_BUNDLE:
      return {
        ...state,
        bundles: [...state.bundles, action.payload],
        dirtySections: new Set([...state.dirtySections, 'bundles']),
      };
      
    case ACTIONS.REMOVE_BUNDLE:
      return {
        ...state,
        bundles: state.bundles.filter(b => b.id !== action.payload),
        dirtySections: new Set([...state.dirtySections, 'bundles']),
      };
    
    // NEW: Update bundle properties (monthly_cost, actual_cost, etc.)
    case ACTIONS.UPDATE_BUNDLE: {
      const { id, ...updates } = action.payload;
      return {
        ...state,
        bundles: state.bundles.map(b => 
          b.id === id ? { ...b, ...updates } : b
        ),
        // Don't mark dirty - save happens immediately in updateBundle
      };
    }
    
    // ==================
    // Pending Changes (Dependency Confirmations)
    // ==================
    case ACTIONS.SET_PENDING_CHANGES:
      return { ...state, pendingChanges: action.payload };
      
    case ACTIONS.CONFIRM_PENDING_CHANGES: {
      if (!state.pendingChanges) return state;
      
      const { type } = state.pendingChanges;
      
      // Apply the pending change based on type
      switch (type) {
        case 'ISP_CHANGE':
          return {
            ...state,
            profile: { ...state.profile, internetProvider: state.pendingChanges.newValue },
            pendingChanges: null,
            dirtySections: new Set([...state.dirtySections, 'isp']),
          };
          
        case 'REMOVE_CARD':
          return {
            ...state,
            creditCards: state.creditCards.filter(c => c.id !== state.pendingChanges.cardId),
            allocations: state.allocations.filter(a => a.credit_card_id !== state.pendingChanges.cardId),
            pendingChanges: null,
            dirtySections: new Set([...state.dirtySections, 'creditCards']),
          };
          
        case 'REMOVE_MEMBERSHIP':
          return {
            ...state,
            memberships: state.memberships.filter(m => m.id !== state.pendingChanges.membershipId),
            pendingChanges: null,
            dirtySections: new Set([...state.dirtySections, 'memberships']),
          };
          
        default:
          return { ...state, pendingChanges: null };
      }
    }
    
    case ACTIONS.CANCEL_PENDING_CHANGES:
      return { ...state, pendingChanges: null };
    
    // ==================
    // UI State
    // ==================
    case ACTIONS.SET_ACTIVE_SECTION:
      return { ...state, activeSection: action.payload };
      
    case ACTIONS.TOGGLE_SECTION: {
      const section = action.payload;
      const expanded = new Set(state.expandedSections);
      
      if (expanded.has(section)) {
        expanded.delete(section);
      } else {
        expanded.add(section);
      }
      
      return { ...state, expandedSections: Array.from(expanded) };
    }
    
    case ACTIONS.SET_SECTION_DIRTY:
      return {
        ...state,
        dirtySections: new Set([...state.dirtySections, action.payload]),
      };
      
    case ACTIONS.CLEAR_SECTION_DIRTY: {
      const newDirty = new Set(state.dirtySections);
      newDirty.delete(action.payload);
      return { ...state, dirtySections: newDirty };
    }
    
    default:
      return state;
  }
}

/**
 * Selectors for computed values
 */
export const selectors = {
  // Total monthly out-of-pocket cost
  getTotalMonthlyCost: (state) => {
    return state.subscriptions.reduce((total, sub) => {
      if (sub.payment_method === 'paid' || !sub.payment_method) {
        return total + parseFloat(sub.actual_cost || sub.monthly_cost || 0);
      }
      if (sub.payment_method === 'credit_card_perk' || sub.payment_method === 'card_perk') {
        const credit = parseFloat(sub.credit_applied || 0);
        const cost = parseFloat(sub.monthly_cost || 0);
        return total + Math.max(0, cost - credit);
      }
      return total; // Free via membership/ISP
    }, 0);
  },
  
  // Total credit card credits available
  getTotalCreditsAvailable: (state) => {
    return state.creditCards.reduce((total, card) => {
      return total + parseFloat(card.streaming_credit_monthly || 0);
    }, 0);
  },
  
  // Total credits allocated
  getTotalCreditsAllocated: (state) => {
    return state.allocations.reduce((total, a) => {
      return total + parseFloat(a.amount_allocated || 0);
    }, 0);
  },
  
  // Unused credits
  getUnusedCredits: (state) => {
    const available = selectors.getTotalCreditsAvailable(state);
    const allocated = selectors.getTotalCreditsAllocated(state);
    return Math.max(0, available - allocated);
  },
  
  // Get allocations for a specific card
  getCardAllocations: (state, cardId) => {
    return state.allocations.filter(a => a.credit_card_id === cardId);
  },
  
  // Check if a section has unsaved changes
  isSectionDirty: (state, section) => {
    return state.dirtySections.has(section);
  },
  
  // Get services available for credit allocation
  getServicesForCreditAllocation: (state) => {
    return state.subscriptions.filter(sub => 
      !sub.payment_method || 
      sub.payment_method === 'paid' || 
      sub.payment_method === 'credit_card_perk'
    );
  },
};